import { ControlProgramId } from "api/generated/model";
import { RiskDirectoryId } from "org/risks/types";

import { EntityId } from "./convert/risks/common";

interface PageKeyUsers {
    type: "Users";
}
interface PageKeyUserGroups {
    type: "UserGroups";
}
interface PageKeyControlsAll {
    type: "ControlsAll";
}
interface PageKeyControlsWeekly {
    type: "ControlsWeekly";
}
interface PageKeyControlsMonthly {
    type: "ControlsMonthly";
}
interface PageKeyControlsTwoMonths {
    type: "ControlsTwoMonths";
}
interface PageKeyControlsQuarterly {
    type: "ControlsQuarterly";
}
interface PageKeyControlsHalfYearly {
    type: "ControlsHalfYearly";
}
interface PageKeyControlsYearly {
    type: "ControlsYearly";
}
interface PageKeyControlsManual {
    type: "ControlsManual";
}
interface PageKeyControlPrograms {
    type: "ControlPrograms";
}
interface PageKeyActivities {
    type: "Activities";
    scope: PageKeyActivitiesScope;
}
interface PageKeyDataSheetReport {
    type: "DataSheetReport";
    scope: PageKeyDataSheetReportScope;
}
interface PageKeyReportsAll {
    type: "ReportsAll";
}
interface PageKeyReportsStatus {
    type: "ReportsStatus";
}
interface PageKeyReportsClassification {
    type: "ReportsClassification";
}
interface PageKeyReportsDatasheet {
    type: "ReportsDatasheet";
}
interface PageKeyReportsWorkload {
    type: "ReportsWorkload";
}
interface PageKeyRiskMap {
    type: "RiskMap";
    riskDirectoryId: RiskDirectoryId;
    entityId?: EntityId;
}
interface PageKeyRiskDirectory {
    type: "RiskDirectory";
    riskDirectoryId: RiskDirectoryId;
}
interface PageKeyControlProgram {
    type: "ControlProgram";
    id: ControlProgramId;
}
interface PageKeyTags {
    type: "Tags";
}
interface PageKeyDashboard {
    type: "Dashboard";
}

type PageKeyControls =
    | PageKeyControlsAll
    | PageKeyControlsWeekly
    | PageKeyControlsMonthly
    | PageKeyControlsTwoMonths
    | PageKeyControlsQuarterly
    | PageKeyControlsHalfYearly
    | PageKeyControlsYearly
    | PageKeyControlsManual;

type PageKeyReports =
    | PageKeyReportsAll
    | PageKeyReportsStatus
    | PageKeyReportsClassification
    | PageKeyReportsWorkload
    | PageKeyReportsDatasheet;

type PageKey =
    | PageKeyControls
    | PageKeyUsers
    | PageKeyUserGroups
    | PageKeyControlPrograms
    | PageKeyActivities
    | PageKeyDataSheetReport
    | PageKeyRiskMap
    | PageKeyRiskDirectory
    | PageKeyTags
    | PageKeyControlProgram
    | PageKeyReports
    | PageKeyDashboard;

// Provides a way to construct a key for a particular page.
const PAGE_KEY = {
    controlsAll: (): PageKeyControlsAll => ({ type: "ControlsAll" }),
    controlsWeekly: (): PageKeyControlsWeekly => ({ type: "ControlsWeekly" }),
    controlsMonthly: (): PageKeyControlsMonthly => ({
        type: "ControlsMonthly",
    }),
    controlsTwoMonths: (): PageKeyControlsTwoMonths => ({
        type: "ControlsTwoMonths",
    }),
    controlsQuarterly: (): PageKeyControlsQuarterly => ({
        type: "ControlsQuarterly",
    }),
    controlsHalfYearly: (): PageKeyControlsHalfYearly => ({
        type: "ControlsHalfYearly",
    }),
    controlsYearly: (): PageKeyControlsYearly => ({ type: "ControlsYearly" }),
    controlsManual: (): PageKeyControlsManual => ({ type: "ControlsManual" }),
    userGroups: (): PageKeyUserGroups => ({ type: "UserGroups" }),
    users: (): PageKeyUsers => ({ type: "Users" }),
    controlPrograms: (): PageKeyControlPrograms => ({
        type: "ControlPrograms",
    }),
    controlProgram: (id: ControlProgramId): PageKeyControlProgram => ({
        type: "ControlProgram",
        id,
    }),
    activities: (args: ActivitiesArgs): PageKeyActivities => {
        const { controlProgramId, controlId } = args;
        if (controlProgramId !== null && controlProgramId !== undefined) {
            return {
                type: "Activities",
                scope: { type: "ControlProgram", controlProgramId },
            };
        } else if (controlId !== null && controlId !== undefined) {
            return {
                type: "Activities",
                scope: { type: "Control", controlId },
            };
        } else {
            return { type: "Activities", scope: { type: "None" } };
        }
    },
    dataSheetReport: (dataSheetId: string): PageKeyDataSheetReport => {
        return {
            type: "DataSheetReport",
            scope: { type: "DataSheet", dataSheetId },
        };
    },
    reportsAll: (): PageKeyReportsAll => ({ type: "ReportsAll" }),
    reportsStatus: (): PageKeyReportsStatus => ({ type: "ReportsStatus" }),
    reportsClassification: (): PageKeyReportsClassification => ({
        type: "ReportsClassification",
    }),
    reportsDatasheet: (): PageKeyReportsDatasheet => ({
        type: "ReportsDatasheet",
    }),
    reportsWorkload: (): PageKeyReportsWorkload => ({
        type: "ReportsWorkload",
    }),
    riskMap: (
        riskDirectoryId: RiskDirectoryId,
        entityId: EntityId | undefined
    ): PageKeyRiskMap => ({ type: "RiskMap", riskDirectoryId, entityId }),
    riskDirectory: (
        riskDirectoryId: RiskDirectoryId
    ): PageKeyRiskDirectory => ({ type: "RiskDirectory", riskDirectoryId }),
    tags: (): PageKeyTags => ({ type: "Tags" }),
    dashboard: (): PageKeyDashboard => ({ type: "Dashboard" }),
};

type PageKeyActivitiesScope =
    | { type: "None" }
    | { type: "ControlProgram"; controlProgramId: number }
    | { type: "Control"; controlId: string };

interface ActivitiesArgs {
    controlProgramId?: number | null;
    controlId?: string | null;
}

interface PageKeyDataSheetReportScope {
    type: "DataSheet";
    dataSheetId: string;
}

export type { PageKey, PageKeyControls, PageKeyReports };
export { PAGE_KEY };
